
// client/src/components/TenantSettings.jsx
import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import {
  Settings as SettingsIcon,
  Building2,
  Shield,
  Palette,
  Eye,
  Bell,
  Globe,
  Upload,
  FileText,
  Sun,
  Moon,
  Monitor,
  RefreshCw,
} from "lucide-react";
import AppFooter from "./AppFooter";
import { FaTimes, FaSave, FaArrowLeft, FaHome } from "react-icons/fa";
import PushNotificationSettings from './PushNotificationSettings';

const baseUrl = process.env.REACT_APP_BASE_URL || "http://localhost:5000";

export default function TenantSettings() {
  const { tenant } = useParams();
  const navigate = useNavigate();

  // -------- state
  const [tenantData, setTenantData] = useState({
    id: null,
    ownerid: null,
    companyname: "",
    requiremfa: false,
    domain: "",
    email: "",
    address1: "",
    address2: "",
    city: "",
    state: "",
    zip: "",
    phone: "",
    logo: null,
    waiver: null,
    active: true,
  });

  const [pwaSettings, setPwaSettings] = useState({
    theme: "system", // 'light' | 'dark' | 'system'
    reducedMotion: false,
    language: "en-US",
    notifications: { enabled: false, marketing: false, security: true, updates: true },
  });

  const [activeTab, setActiveTab] = useState("company");
  const [busy, setBusy] = useState(false);
  const [status, setStatus] = useState("");
  const [showInCollabDirectory, setShowInCollabDirectory] = useState(true);

  // -------- helpers
  const getAssetUrl = (p) => {
    if (!p) return "";
    if (p.startsWith("http://") || p.startsWith("https://")) return p;
    return `${baseUrl}${p}`;
  };

  const tabs = [
    { id: "company", label: "Company", icon: Building2 },
    { id: "security", label: "Security", icon: Shield },
    { id: "appearance", label: "Appearance", icon: Palette },
    { id: "accessibility", label: "Accessibility", icon: Eye },
    { id: "notifications", label: "Notifications", icon: Bell },
    { id: "language", label: "Language", icon: Globe },
    { id: "collaboration", label: "Collaboration", icon: Eye },
  ];

  // -------- effects
  useEffect(() => {
    (async () => {
      setBusy(true);
      try {
        // Load tenant data from API using tenant ID from URL
        const res = await axios.get(`${baseUrl}/api/tenants/${tenant}/settings`, {
          withCredentials: true,
        });
        
        console.log('Tenant settings response:', res.data);
        
        if (res.data?.success && res.data?.tenant) {
          setTenantData(res.data.tenant);
        } else {
          setStatus("Could not load tenant data");
          console.error("Invalid response format:", res.data);
        }

        // Load PWA settings from localStorage
        const saved = localStorage.getItem("pwaSettings");
        if (saved) {
          try {
            setPwaSettings(JSON.parse(saved));
          } catch (e) {
            console.error("Error parsing saved PWA settings:", e);
          }
        }

        // Detect system preferences
        if (window.matchMedia?.("(prefers-reduced-motion: reduce)").matches) {
          setPwaSettings((p) => ({ ...p, reducedMotion: true }));
        }
        if (window.matchMedia?.("(prefers-color-scheme: dark)").matches) {
          document.documentElement.classList.add("dark");
        }
      } catch (e) {
        console.error("Error loading settings:", e);
        const errorMsg = e.response?.data?.message || e.message || "Error loading settings";
        setStatus(`Error: ${errorMsg}`);
      } finally {
        setBusy(false);
      }
    })();
  }, [tenant]);

  // Load Collaboration setting
  useEffect(() => {
    (async () => {
      try {
        const r = await axios.get(`${baseUrl}/api/collab/${tenant}/settings`, {
          withCredentials: true,
        });
        setShowInCollabDirectory(!!r.data?.showInCollabDirectory);
      } catch (e) {
        // Silent fail - not critical
        console.error("Error loading collab settings:", e);
      }
    })();
  }, [tenant]);

  // -------- handlers
  const handleTenantChange = (field, value) =>
    setTenantData((p) => ({ ...p, [field]: value }));

  const handlePwaSettingChange = (field, value) => {
    const next = { ...pwaSettings, [field]: value };
    setPwaSettings(next);
    try {
      localStorage.setItem("pwaSettings", JSON.stringify(next));
    } catch (e) {
      console.error("Error saving PWA settings:", e);
    }
  };

  const handleNotificationToggle = (k, v) =>
    setPwaSettings((p) => ({
      ...p,
      notifications: { ...p.notifications, [k]: v },
    }));

  const handleThemeChange = (theme) => {
    handlePwaSettingChange("theme", theme);
    if (theme === "dark") {
      document.documentElement.classList.add("dark");
    } else if (theme === "light") {
      document.documentElement.classList.remove("dark");
    } else {
      // System preference
      const isDark = window.matchMedia?.("(prefers-color-scheme: dark)").matches;
      document.documentElement.classList.toggle("dark", !!isDark);
    }
  };

  const uploadFileToServer = async (field, file, type) => {
    const formData = new FormData();
    formData.append("file", file);
    formData.append("type", type); // 'logo' | 'waiver'
    
    try {
      setBusy(true);
      const res = await axios.post(
        `${baseUrl}/api/tenants/${tenant}/media`,
        formData,
        {
          withCredentials: true,
          headers: { "Content-Type": "multipart/form-data" },
        }
      );
      
      if (res.data?.success) {
        handleTenantChange(field, res.data.path);
        setStatus(`${type === "logo" ? "Logo" : "Waiver"} uploaded`);
      } else {
        setStatus("Upload failed");
      }
    } catch (e) {
      console.error("Upload error:", e);
      setStatus("Upload error");
    } finally {
      setBusy(false);
      setTimeout(() => setStatus(""), 2500);
    }
  };

  const handleFileUpload = (field, file) => file && uploadFileToServer(field, file, field);

  const requestNotificationPermission = async () => {
    if (!("Notification" in window)) {
      setStatus("Notifications not supported");
      setTimeout(() => setStatus(""), 2000);
      return;
    }

    try {
      const perm = await Notification.requestPermission();
      if (perm === "granted") {
        handleNotificationToggle("enabled", true);
        setStatus("Notifications enabled");
      } else {
        setStatus("Notification permission denied");
      }
      setTimeout(() => setStatus(""), 2000);
    } catch (e) {
      console.error("Notification permission error:", e);
      setStatus("Error requesting permission");
      setTimeout(() => setStatus(""), 2000);
    }
  };

  const saveAll = async () => {
    if (!tenantData.companyname || !tenantData.email) {
      setStatus("Company name and email are required");
      setTimeout(() => setStatus(""), 3000);
      return;
    }

    setBusy(true);
    setStatus("Saving…");

    try {
      await axios.put(
        `${baseUrl}/api/tenants/${tenant}/settings`,
        tenantData,
        {
          withCredentials: true,
          headers: { "Content-Type": "application/json" },
        }
      );

      localStorage.setItem("pwaSettings", JSON.stringify(pwaSettings));
      setStatus("Settings saved successfully");
    } catch (e) {
      console.error("Save error:", e);
      setStatus("Error saving settings");
    } finally {
      setBusy(false);
      setTimeout(() => setStatus(""), 3000);
    }
  };

  const goDashboard = () => navigate(`/${tenant}/dashboard`);

  // -------- tab components
  const Company = () => (
    <div>
      <div className="form-group">
        <label htmlFor="companyName">Company Name *</label>
        <input
          id="companyName"
          type="text"
          className="form-control"
          placeholder="Acme Corporation"
          value={tenantData.companyname || ""}
          onChange={(e) => handleTenantChange("companyname", e.target.value)}
        />
      </div>

      <div className="form-group">
        <label htmlFor="domain">Domain</label>
        <input
          id="domain"
          type="text"
          className="form-control"
          placeholder="acme.com"
          value={tenantData.domain || ""}
          onChange={(e) => handleTenantChange("domain", e.target.value)}
        />
      </div>

      <div className="form-group">
        <label htmlFor="email">Email *</label>
        <input
          id="email"
          type="email"
          className="form-control"
          placeholder="contact@acme.com"
          value={tenantData.email || ""}
          onChange={(e) => handleTenantChange("email", e.target.value)}
        />
      </div>

      <div className="form-group">
        <label htmlFor="phone">Phone</label>
        <input
          id="phone"
          type="tel"
          className="form-control"
          placeholder="(555) 123-4567"
          value={tenantData.phone || ""}
          onChange={(e) => handleTenantChange("phone", e.target.value)}
        />
      </div>

      <div className="form-group">
        <label htmlFor="address1">Address Line 1</label>
        <input
          id="address1"
          type="text"
          className="form-control"
          placeholder="123 Main St"
          value={tenantData.address1 || ""}
          onChange={(e) => handleTenantChange("address1", e.target.value)}
        />
      </div>

      <div className="form-group">
        <label htmlFor="address2">Address Line 2</label>
        <input
          id="address2"
          type="text"
          className="form-control"
          placeholder="Suite 100"
          value={tenantData.address2 || ""}
          onChange={(e) => handleTenantChange("address2", e.target.value)}
        />
      </div>

      <div className="row">
        <div className="col-md-6">
          <div className="form-group">
            <label htmlFor="city">City</label>
            <input
              id="city"
              type="text"
              className="form-control"
              placeholder="New York"
              value={tenantData.city || ""}
              onChange={(e) => handleTenantChange("city", e.target.value)}
            />
          </div>
        </div>
        <div className="col-md-3">
          <div className="form-group">
            <label htmlFor="state">State</label>
            <input
              id="state"
              type="text"
              className="form-control"
              placeholder="NY"
              value={tenantData.state || ""}
              onChange={(e) => handleTenantChange("state", e.target.value)}
            />
          </div>
        </div>
        <div className="col-md-3">
          <div className="form-group">
            <label htmlFor="zip">ZIP</label>
            <input
              id="zip"
              type="text"
              className="form-control"
              placeholder="10001"
              value={tenantData.zip || ""}
              onChange={(e) => handleTenantChange("zip", e.target.value)}
            />
          </div>
        </div>
      </div>

      {/* Logo Upload */}
      <div className="form-group">
        <label htmlFor="logoUpload">
          <Upload style={{ width: "16px", height: "16px", marginRight: "6px" }} />
          Company Logo
        </label>
        {tenantData.logo && (
          <div style={{ marginBottom: "10px" }}>
            <img
              src={getAssetUrl(tenantData.logo)}
              alt="Logo preview"
              style={{ maxWidth: "200px", maxHeight: "100px", objectFit: "contain" }}
            />
          </div>
        )}
        <input
          id="logoUpload"
          type="file"
          accept="image/*"
          onChange={(e) => handleFileUpload("logo", e.target.files?.[0])}
        />
      </div>

      {/* Waiver Upload */}
      <div className="form-group">
        <label htmlFor="waiverUpload">
          <FileText style={{ width: "16px", height: "16px", marginRight: "6px" }} />
          Waiver Document (PDF)
        </label>
        {tenantData.waiver && (
          <div style={{ marginBottom: "10px", fontSize: "14px", color: "#666" }}>
            Current waiver: <a href={getAssetUrl(tenantData.waiver)} target="_blank" rel="noreferrer">View</a>
          </div>
        )}
        <input
          id="waiverUpload"
          type="file"
          accept=".pdf"
          onChange={(e) => handleFileUpload("waiver", e.target.files?.[0])}
        />
      </div>
    </div>
  );

  const Security = () => (
    <div>
      <div className="form-group">
        <label className="flex items-center gap-3">
          <input
            type="checkbox"
            checked={tenantData.requiremfa || false}
            onChange={(e) => handleTenantChange("requiremfa", e.target.checked)}
          />
          <span>Require Multi-Factor Authentication (MFA)</span>
        </label>
        <p style={{ fontSize: "14px", color: "#666", marginTop: "8px" }}>
          Users must use MFA to access the application
        </p>
      </div>

      <div style={{ marginTop: "20px" }}>
        <h4 style={{ fontWeight: "600", marginBottom: "10px" }}>Security Features</h4>
        <ul style={{ listStyle: "disc", paddingLeft: "20px", fontSize: "14px", color: "#666" }}>
          <li>End-to-end encryption for data in transit</li>
          <li>Secure session management</li>
          <li>Regular security audits</li>
          <li>GDPR & CCPA compliant</li>
        </ul>
      </div>
    </div>
  );

  const Appearance = () => (
    <div>
      <div className="form-group">
        <label>Theme</label>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: "10px", marginTop: "10px" }}>
          {[
            { id: "light", label: "Light", icon: Sun },
            { id: "dark", label: "Dark", icon: Moon },
            { id: "system", label: "System", icon: Monitor },
          ].map(({ id, label, icon: Icon }) => {
            const active = pwaSettings.theme === id;
            return (
              <button
                key={id}
                type="button"
                onClick={() => handleThemeChange(id)}
                className={`btn btn-sm ${active ? "btn-primary" : "btn-outline-primary"}`}
                style={{ 
                  display: "flex", 
                  flexDirection: "column", 
                  alignItems: "center", 
                  gap: "6px",
                  padding: "12px 8px"
                }}
              >
                <Icon style={{ width: "20px", height: "20px" }} />
                <span style={{ fontSize: "12px" }}>{label}</span>
              </button>
            );
          })}
        </div>
      </div>

      <div style={{ marginTop: "20px" }}>
        <h4 style={{ fontWeight: "600", marginBottom: "10px" }}>Available Themes</h4>
        <ul style={{ listStyle: "disc", paddingLeft: "20px", fontSize: "14px", color: "#666" }}>
          <li><strong>Light:</strong> Clean, bright interface</li>
          <li><strong>Dark:</strong> Reduced eye strain in low-light</li>
          <li><strong>System:</strong> Follows your device preferences</li>
        </ul>
      </div>
    </div>
  );

  const Accessibility = () => (
    <div>
      <div className="form-group">
        <label className="flex items-center gap-3">
          <input
            type="checkbox"
            checked={pwaSettings.reducedMotion}
            onChange={(e) => handlePwaSettingChange("reducedMotion", e.target.checked)}
          />
          <span>Reduce motion and animations</span>
        </label>
        <p style={{ fontSize: "14px", color: "#666", marginTop: "8px" }}>
          Minimizes animations for users sensitive to motion
        </p>
      </div>

      <div style={{ marginTop: "20px" }}>
        <h4 style={{ fontWeight: "600", marginBottom: "10px" }}>Built-in Features</h4>
        <ul style={{ listStyle: "disc", paddingLeft: "20px", fontSize: "14px", color: "#666" }}>
          <li>High contrast support</li>
          <li>Keyboard navigation</li>
          <li>Screen reader compatibility</li>
          <li>Touch/stylus input support</li>
        </ul>
      </div>
    </div>
  );

  const Notifications = () => (
    <div>
      {/* Push Notification Settings Component */}
      <PushNotificationSettings 
        showTestButton={true}
        onStatusChange={(status) => {
          console.log('Notification status changed:', status);
          // Update local state if needed
          if (status.enabled) {
            handleNotificationToggle("enabled", true);
          }
        }}
      />

      {/* Additional notification preferences */}
      {pwaSettings.notifications.enabled && (
        <div style={{ marginTop: "30px" }}>
          <h4 style={{ fontWeight: "600", marginBottom: "15px", color: "#01057a" }}>
            Notification Preferences
          </h4>
          <div style={{ paddingLeft: "20px", borderLeft: "3px solid #01057a" }}>
            {[
              { key: "security", label: "Security alerts", description: "Get notified about security-related events" },
              { key: "updates", label: "App updates", description: "Receive notifications about new features and updates" },
              { key: "marketing", label: "Marketing", description: "Promotional offers and announcements" },
            ].map((o) => (
              <div key={o.key} className="form-group" style={{ marginBottom: "15px" }}>
                <label className="flex items-center gap-3">
                  <input
                    type="checkbox"
                    checked={pwaSettings.notifications[o.key]}
                    onChange={(e) => handleNotificationToggle(o.key, e.target.checked)}
                  />
                  <div>
                    <div style={{ fontWeight: "500" }}>{o.label}</div>
                    <div style={{ fontSize: "12px", color: "#666" }}>{o.description}</div>
                  </div>
                </label>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );

  const Collaboration = () => (
    <div>
      <div className="form-group">
        <label className="flex items-center gap-3">
          <input
            type="checkbox"
            checked={showInCollabDirectory}
            onChange={async (e) => {
              const next = e.target.checked;
              setShowInCollabDirectory(next);
              try {
                await axios.patch(
                  `${baseUrl}/api/collab/${tenant}/settings`,
                  { showInCollabDirectory: next },
                  { withCredentials: true }
                );
                setStatus("Collaboration setting updated");
                setTimeout(() => setStatus(""), 2000);
              } catch (err) {
                console.error(err);
                setStatus("Error updating setting");
                setTimeout(() => setStatus(""), 2000);
                setShowInCollabDirectory(!next);
              }
            }}
          />
          <span>Show in collaboration directory</span>
        </label>
        <p style={{ fontSize: "14px", color: "#666", marginTop: "8px" }}>
          Allow other tenants to discover and collaborate with you
        </p>
      </div>
    </div>
  );

  const Language = () => (
    <div>
      <div className="form-group">
        <label htmlFor="language">Language</label>
        <select
          id="language"
          className="form-control"
          value={pwaSettings.language}
          onChange={(e) => handlePwaSettingChange("language", e.target.value)}
        >
          <option value="en-US">English (US)</option>
          <option value="en-GB">English (UK)</option>
          <option value="es-ES">Español (España)</option>
          <option value="es-MX">Español (México)</option>
          <option value="fr-FR">Français</option>
          <option value="de-DE">Deutsch</option>
          <option value="it-IT">Italiano</option>
          <option value="pt-BR">Português (Brasil)</option>
          <option value="ja-JP">日本語</option>
          <option value="ko-KR">한국어</option>
          <option value="zh-CN">中文 (简体)</option>
          <option value="zh-TW">中文 (繁體)</option>
        </select>
      </div>
      <p style={{ fontSize: "14px", color: "#666", marginTop: "8px" }}>
        Language changes take effect after refreshing the app
      </p>
    </div>
  );

  // -------- render
  if (busy && !tenantData.id) {
    return <div className="container py-4"><p>Loading settings...</p></div>;
  }

  return (
    <div className="container">
      <div style={{ textAlign: "center", marginBottom: "20px", position: "relative" }}>
        <h2 className="hdr hdr--primary hdr--lg">Settings</h2>
        {busy && (
          <RefreshCw 
            style={{ 
              width: "20px", 
              height: "20px", 
              animation: "spin 1s linear infinite",
              position: "absolute",
              right: "10px",
              top: "50%",
              transform: "translateY(-50%)"
            }} 
          />
        )}
      </div>

      {status && (
        <div
          style={{
            marginBottom: "20px",
            padding: "12px",
            borderRadius: "8px",
            backgroundColor: status.includes("Error") ? "#fee2e2" : "#d1fae5",
            color: status.includes("Error") ? "#991b1b" : "#065f46",
            fontSize: "14px",
          }}
        >
          {status}
        </div>
      )}

      {/* Tab grid */}
      <div style={{ 
        display: "grid", 
        gridTemplateColumns: "repeat(2, 1fr)", 
        gap: "8px", 
        marginBottom: "20px" 
      }}>
        {tabs.map(({ id, label, icon: Icon }) => {
          const active = activeTab === id;
          return (
            <button
              key={id}
              type="button"
              onClick={() => setActiveTab(id)}
              className={`btn btn-sm ${active ? "btn-primary" : "btn-outline-primary"}`}
              style={{ 
                display: "flex", 
                alignItems: "center", 
                justifyContent: "center", 
                gap: "4px",
                padding: "8px 4px",
                fontSize: "12px",
                minHeight: "36px"
              }}
            >
              <Icon style={{ width: "14px", height: "14px", flexShrink: 0 }} />
              <span style={{ lineHeight: "1.2" }}>{label}</span>
            </button>
          );
        })}
      </div>

      {/* Body */}
      <div style={{ marginBottom: "80px" }}>
        {activeTab === "company" && <Company />}
        {activeTab === "security" && <Security />}
        {activeTab === "appearance" && <Appearance />}
        {activeTab === "accessibility" && <Accessibility />}
        {activeTab === "notifications" && <Notifications />}
        {activeTab === "language" && <Language />}
        {activeTab === "collaboration" && <Collaboration />}
      </div>

      {/* Footer actions */}
      <AppFooter
        buttons={[
          {
            icon: <FaArrowLeft />,
            label: "Back",
            onClick: () => navigate(-1),
          },
          { icon: <FaHome />, label: "Home", onClick: goDashboard },
          {
            icon: busy ? <RefreshCw className="animate-spin" /> : <FaSave />,
            label: busy ? "Saving…" : "Save",
            onClick: saveAll,
            type: "button",
            disabled: busy,
          },
        ]}
      />
    </div>
  );
}
